import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Typography,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  Box,
} from "@mui/material";

//  Which patients a medicine may be prescribed to, and the way past the filter
//  when the patient genuinely needs something outside their band.
import { AgeBandChip, AgeBandNotice } from './MedicationAgeBand';
import Send from "@mui/icons-material/Send";
import Close from "@mui/icons-material/Close";


import axios from "axios";
import {
  loadMedicationsdb,
  addMedicationdb,
  loadRequestedMedsInpatient,
  loadMedicationByAge,
} from "../BaseURL";

import {
  frequencyMap,
  frequencyMultiplier,
  standardFrequencies,
  MEDICATION_ROUTES,
} from "../Message";

import Swal from "sweetalert2";
import moment from "moment";

const MedicationRequestDialog = ({ open, onClose, patient, currentUser ,sendstartDate }) => {
  // ---------- BASIC STATE ----------

 const currentDate = moment().format("YYYY-MM-DD");

  const [loading, setLoading] = useState(false);

  // Available medications
  const [medications, setMedications] = useState([]);
  const [medSearchTerm, setMedSearchTerm] = useState("");
  const [medPage, setMedPage] = useState(0);
  const [medRowsPerPage, setMedRowsPerPage] = useState(10);

  // Requested medications
  const [requestedMeds, setRequestedMeds] = useState([]);
  const [requestDate, setRequestDate] = useState("");



  // Form state
  const [selectedMed, setSelectedMed] = useState(null);
  const [doseAmount, setDoseAmount] = useState("");
  const [doseUnit, setDoseUnit] = useState("");
  const [frequency, setFrequency] = useState("");
  const [days, setDays] = useState("");
  const [route, setRoute] = useState("");
  const [qty, setQty] = useState(0);
  const [autoCalculate, setAutoCalculate] = useState(true);

  const [editingIndex, setEditingIndex] = useState(null);
  const [editingMedRow, setEditingMedRow] = useState(null);

  // Validation flags
  const [doseAmountError, setDoseAmountError] = useState(false);
  const [frequencyError, setFrequencyError] = useState(false);
  const [daysError, setDaysError] = useState(false);
  const [routeError, setRouteError] = useState(false);
  const [qtyError, setQtyError] = useState(false);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const showSnack = (msg) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  };

  // ================= HELPERS =================

  const normalizeFrequency = (freq) => {
    if (!freq) return "";
    return frequencyMap[freq.toLowerCase().trim()] || freq;
  };

  const autoCalcUnits = [
    "tab", "tablet",
    "cap", "capsule",
    "pill", "lozenge", "suppository",
    "amp", "vial"
  ];

  const isAutoCalcUnit = (unit = "") =>
    autoCalcUnits.some((u) => unit.toLowerCase().includes(u));

  const solidKeywords = ["tablet", "tab", "capsule", "cap", "pill", "lozenge", "suppository"];
  const isSolidByName = (name = "") =>
    solidKeywords.some((k) => name.toLowerCase().includes(k));

  // Unicode fraction mapping
  const FRACTION_MAP = { "¼": 0.25, "½": 0.5, "¾": 0.75 };
  const normalizeUnicodeFractions = (s) =>
    String(s || "").replace(/[¼½¾]/g, (m) => String(FRACTION_MAP[m] ?? m));

  // Extract numeric dose from string like "½", "1/2", "500mg", etc.
  const parseDoseNumber = (doseStr) => {
    if (!doseStr) return 0;
    const cleaned = normalizeUnicodeFractions(doseStr).trim().toLowerCase();
    const m = cleaned.match(/^(\d+(?:\.\d+)?|\d+\/\d+)/);
    if (!m) return 0;
    if (m[1].includes("/")) {
      const [a, b] = m[1].split("/").map(Number);
      return b ? a / b : Number(m[1]) || 0;
    }
    return parseFloat(m[1]) || 0;
  };

  // Quantity computation
  const computeQty = ({
    doseAmount,
    doseString,
    days,
    frequency,
    doseUnit,
    normalizeFrequencyFn,
    freqMultiplierMap,
  }) => {
    const normalized = normalizeFrequencyFn(frequency || "");
    const perDay = freqMultiplierMap[normalized] ?? 1;

    const amount =
      typeof doseAmount === "number" && !isNaN(doseAmount)
        ? doseAmount
        : parseDoseNumber(doseString);

    if (!amount || !Number(days)) return 0;

    return Math.ceil(Number(amount) * Number(perDay) * Number(days));
  };

  const extractStrengthFromName = (name) => {
    if (!name) return { medName: "", medStrength: "" };

    let medName = name.trim();
    let medStrength = "";

    if (name.includes(",")) {
      const parts = name.split(",");
      medName = parts.slice(0, -1).join(",").trim();
      medStrength = parts[parts.length - 1].trim();
    } else {
      const parenMatch = name.match(/^(.*)\(([^()]*)\)\s*$/);
      if (parenMatch) {
        medName = parenMatch[1].trim();
        medStrength = parenMatch[2].trim();
      } else {
        const match = name.match(
          /(.*?)(\d+(?:\.\d+)?\s?(?:mg\/ml|mg|g|mcg|ml|mls|%|units?|iu))$/i
        );
        if (match) {
          medName = match[1].trim();
          medStrength = match[2].trim();
        }
      }
    }

    return { medName, medStrength };
  };

  const loadLastMedicationPattern = async (selectedMedName, patientAge) => {
    if (!patient) return;
    try {
      const ageGroup = patientAge >= 12 ? "adult" : "child";

      const res = await axios.get(loadMedicationByAge, {
        params: { name: selectedMedName, age: ageGroup },
      });

      if (res.data.success) {
        const p = res.data.med;
        setDoseAmount(p.dose);
        setFrequency(p.freq);
        setDays(p.day_server);
        setRoute(p.rou);
        setQty(p.qua);
      } else {
        setDoseAmount("");
        setFrequency("");
        setDays("");
        setRoute("");
        setQty(0);
      }
    } catch (error) {
      console.error("Error loading medication pattern:", error);
    }
  };

  // ================= DATA LOADING =================

  /*  Whether the prescriber has asked to see past the age filter. See
   *  MedicationAgeBand.js — a drug hidden by its band must always be reachable,
   *  because the alternative is a medicine that cannot be given at all. */
  const [showAllAges, setShowAllAges] = useState(false);
  const [medAgeMeta, setMedAgeMeta] = useState(null);

  const loadMedications = async (allAges = showAllAges) => {
    if (!patient) return;
    setLoading(true);
    try {
      const response = await axios.get(loadMedicationsdb, {
        params: {
          insurance: patient.insurance,
          patientId: patient.id,
          //  'all' is the documented opt-out the endpoint has always supported
          //  and no screen ever sent.
          ...(allAges ? { ageCategory: 'all' } : null),
        },
      });
      setMedAgeMeta(response.data);
      if (response.data.success) {
        setMedications(response.data.results || []);
      } else {
        setMedications([]);
      }
    } catch (error) {
      console.error("Failed to load medications:", error);
    } finally {
      setLoading(false);
    }
  };

 

  // When dialog opens, load everything
 useEffect(() => {
  if (!open || !patient) return;

  handleClearMedication(false);

  // Load today’s date into requestDate
  setRequestDate(moment().format("YYYY-MM-DD"));

  setMedPage(0);

  loadMedications();
  loadRequestedMeds()

}, [open, patient]);


  // ================= AUTO CALCULATE QTY =================

  useEffect(() => {
    if (!autoCalculate) return;

    const numericDoseAmount = parseFloat(normalizeUnicodeFractions(doseAmount)) || 0;

    const nextQty = computeQty({
      doseAmount: numericDoseAmount,
      doseString: doseAmount,
      days,
      frequency,
      doseUnit,
      normalizeFrequencyFn: normalizeFrequency,
      freqMultiplierMap: frequencyMultiplier,
    });

    setQty(nextQty);
  }, [doseAmount, days, frequency, doseUnit, autoCalculate]);

  // ================= ACTIONS =================

  const handleSelectMedication = (med) => {
    setSelectedMed(med);
    const { medStrength } = extractStrengthFromName(med.name);
    setDoseUnit(medStrength || "");
    setAutoCalculate(isSolidByName(med.name) || isAutoCalcUnit(medStrength));
    // Load last pattern
    loadLastMedicationPattern(med.name, patient.age);
  };

  const validateForm = () => {
    let hasError = false;
    setDoseAmountError(false);
    setFrequencyError(false);
    setDaysError(false);
    setRouteError(false);
    setQtyError(false);

    if (!selectedMed) {
      showSnack("❌ Please select a medication first");
      return false;
    }

    if (!doseAmount) {
      setDoseAmountError(true);
      hasError = true;
    }

    if (!frequency) {
      setFrequencyError(true);
      hasError = true;
    }

    if (!days || Number(days) <= 0) {
      setDaysError(true);
      hasError = true;
    }

    if (!route) {
      setRouteError(true);
      hasError = true;
    }

    if (
      (!autoCalculate && (!qty || qty <= 0)) ||
      (autoCalculate && qty <= 0)
    ) {
      setQtyError(true);
      hasError = true;
    }

    if (hasError) {
      showSnack("❌ Please correct validation errors");
      return false;
    }

    return true;
  };


const loadRequestedMeds = async () => {
   setLoading(true);
  try {
    const response = await axios.get(loadRequestedMedsInpatient, {
      params: { patientId: patient.id , dat:sendstartDate, currentDate}
    });

    if (response.data.success) {
      setRequestedMeds(response.data.results);
    }
  } catch (err) {
    console.error("Error loading meds:", err);
  }finally {
    setLoading(false);
  }
};


 
const handleAddMedication = async () => {
  if (!patient) return;

  // ---------------- BASIC FIELD VALIDATION ----------------
  if (!validateForm()) return;

  // ---------------- DUPLICATE MEDICATION CHECK ----------------
   const alreadyRequested = requestedMeds.some(
     (r) =>
       r.drug == selectedMed.name  && r.date==currentDate            // ✅ exact match with backend date
   );
 
   if (alreadyRequested) {
          const result = await Swal.fire({
            title: "Duplicate Request",
            text: `${selectedMed.name} has already been requested today. Do you want to request again?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, Request Again",
            cancelButtonText: "No"
          });
      
          if (!result.isConfirmed) {
            return; // user cancelled
          }
        }

  // ---------------- DATE VALIDATION ----------------

  if (!requestDate) {
    showSnack("❌ Please select a request date");
    return;
  }

  if (moment(requestDate).isBefore(moment(sendstartDate))) {
    showSnack(
      `❌ Date cannot be earlier than admission date (${sendstartDate})`
    );
    return;
  }

  if (moment(requestDate).isAfter(moment(currentDate))) {
    showSnack("❌ Date cannot be in the future");
    return;
  }
  

  // ---------------- SAVE MEDICATION REQUEST ----------------
  setLoading(true);
  try {
    const response = await axios.post(addMedicationdb, {
      patientId: patient.id,
      selectedMed,
      qty,
      frequency,
      route,
      doseAmount,
      days,
      currentUser,
      insurance: patient.insurance,
      dat: requestDate, 
      age: patient.age,
    });

    if (response.data.success) {
      showSnack(`✅ ${selectedMed.name} added successfully`);
      handleClearMedication(false);
    } else {
      showSnack(`❌ ${response.data.message || "Failed to save medication"}`);
    }
  } catch (err) {
    console.error(err);
    const serverMsg = err.response?.data?.message;
    showSnack(`❌ ${serverMsg || "Failed to save medication"}`);
  } finally {
    setLoading(false);
  }
};

  const handleClearMedication = (resetSelected = true) => {
    if (resetSelected) setSelectedMed(null);
    setDoseAmount("");
    setDoseUnit("");
    setFrequency("");
    setDays("");
    setQty(0);
    setRoute("");
    setAutoCalculate(true);

    setDoseAmountError(false);
    setFrequencyError(false);
    setDaysError(false);
    setRouteError(false);
    setQtyError(false);

    setEditingIndex(null);
    setEditingMedRow(null);
  };

  const handleCloseDialog = () => {
    handleClearMedication();
    onClose && onClose();
  };

  // ================= RENDER =================

  return (
    <>
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle  sx={{
            background: "linear-gradient(135deg, #19b934ff 0%, #19b934ff 100%)",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 700,
          }}>
          Request Medication
          <IconButton sx={{ color: "white" }} onClick={handleCloseDialog}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Global loading overlay inside dialog */}
          {loading && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(255,255,255,0.5)",
                zIndex: 10,
              }}
            >
              <Typography variant="body2">Processing...</Typography>
            </Stack>
          )}

          <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
            {/* LEFT: AVAILABLE MEDS */}
            <Grid item xs={12} md={4} size={8}>
              <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Available Medications
                </Typography>

               <Grid container spacing={1} columns={{ xs: 4, sm: 8, md: 12 }}>
                <Grid item xs={6} size={6}>
                  <TextField
                    fullWidth
                    placeholder="Search medication..."
                    value={medSearchTerm}
                    onChange={(e) => setMedSearchTerm(e.target.value)}
                    size="small"
                    margin="dense"
                  />
                </Grid>

                <Grid item xs={6} size={6}>
                  <TextField
                    label="Request Date"
                    type="date"
                    fullWidth
                    size="small"
                    margin="dense"
                    value={requestDate}
                    onChange={(e) => setRequestDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>


                <AgeBandNotice meta={medAgeMeta} showAll={showAllAges}
                  onToggle={() => {
                    const next = !showAllAges;
                    setShowAllAges(next);
                    setMedPage(0);
                    loadMedications(next);
                  }} />

                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Medication</TableCell>
                        <TableCell align="center">Select</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {medications
                        .filter((med) =>
                          med.name
                            .toLowerCase()
                            .includes(medSearchTerm.toLowerCase())
                        )
                        .slice(
                          medPage * medRowsPerPage,
                          medPage * medRowsPerPage + medRowsPerPage
                        )
                        .map((med, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {med.name}
                                <AgeBandChip value={med.age_cat} />
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleSelectMedication(med)}
                              >
                                <Send fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={medications.filter((med) =>
                    med.name.toLowerCase().includes(medSearchTerm.toLowerCase())
                  ).length}
                  page={medPage}
                  onPageChange={(e, newPage) => setMedPage(newPage)}
                  rowsPerPage={medRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setMedRowsPerPage(parseInt(e.target.value, 10));
                    setMedPage(0);
                  }}
                  rowsPerPageOptions={[10, 20, 30]}
                />
              </Paper>
            </Grid>

            {/* CENTER: MEDICATION REQUEST FORM */}
            <Grid item xs={12} md={4} size={4}>
              <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Medication Request
                </Typography>

                <TextField
                  label="Selected Medication"
                  value={selectedMed?.name || ""}
                  fullWidth
                  margin="dense"
                  disabled
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#000",
                      opacity: 1,
                    },
                    "& .MuiInputLabel-root.Mui-disabled": {
                      color: "#000",
                      opacity: 1,
                    },
                  }}
                />

                <TextField
                  label="Medication Status"
                  value={selectedMed?.status || ""}
                  fullWidth
                  margin="dense"
                  disabled
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#000",
                      opacity: 1,
                    },
                    "& .MuiInputLabel-root.Mui-disabled": {
                      color: "#000",
                      opacity: 1,
                    },
                  }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoCalculate}
                      onChange={(e) => setAutoCalculate(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Auto Calculate Quantity"
                  sx={{ mb: 1 }}
                />

                <TextField
                  label="Amount per dose" placeholder="Amount taken at one time"
                  type="text"
                  value={doseAmount}
                  onChange={(e) => setDoseAmount(e.target.value)}
                  fullWidth
                  size="small"
                  margin="dense"
                  error={doseAmountError}
                  helperText={doseAmountError ? "Required" : ""}
                />

                <TextField
                  label="Dose Unit"
                  value={doseUnit}
                  onChange={(e) => setDoseUnit(e.target.value)}
                  fullWidth
                  size="small"
                  margin="dense"
                />

                <FormControl fullWidth margin="dense" size="small">
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    error={frequencyError}
                  >
                    {standardFrequencies.map((freq) => (
                      <MenuItem key={freq} value={freq}>
                        {freq}
                      </MenuItem>
                    ))}
                  </Select>
                  {frequencyError && (
                    <Typography variant="caption" color="error">
                      Required
                    </Typography>
                  )}
                </FormControl>

                <TextField
                  label="Days"
                  type="number"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  fullWidth
                  size="small"
                  margin="dense"
                  error={daysError}
                  helperText={daysError ? "Must be > 0" : ""}
                />

                <FormControl fullWidth margin="dense" size="small">
                  <InputLabel>Route</InputLabel>
                  <Select
                    value={route}
                    onChange={(e) => setRoute(e.target.value)}
                    error={routeError}
                  >
                    {MEDICATION_ROUTES.map((rt) => (
                      <MenuItem key={rt} value={rt}>
                        {rt}
                      </MenuItem>
                    ))}
                  </Select>
                  {routeError && (
                    <Typography variant="caption" color="error">
                      Required
                    </Typography>
                  )}
                </FormControl>

                <TextField
                  label="Quantity"
                  type="number"
                  value={qty}
                  onChange={(e) =>
                    !autoCalculate && setQty(Number(e.target.value))
                  }
                  fullWidth
                  size="small"
                  margin="dense"
                  disabled={autoCalculate}
                  error={qtyError}
                  helperText={
                    qtyError ? "Quantity must be greater than 0" : ""
                  }
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#000",
                      opacity: 1,
                    },
                    "& .MuiInputLabel-root.Mui-disabled": {
                      color: "#000",
                      opacity: 1,
                    },
                  }}
                />

                <Stack spacing={1} direction="row" sx={{ mt: 2 }}>
              
                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      onClick={handleAddMedication}
                    >
                      Make Request
                    </Button>
                  
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => handleClearMedication()}
                  >
                    Clear
                  </Button>
                </Stack>
              </Paper>
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            color="error"
            onClick={handleCloseDialog}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbarMessage.includes("❌") ? "error" : "success"}
          sx={{ fontWeight: 600 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MedicationRequestDialog;
